import type { ReactElement } from 'react'
import { useFormContext, useWatch, type FieldValues } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { renameSWxxToCxxIfApplicable } from '@domainUtils'
import { cloneDeep } from 'lodash-es'
import { Box } from '@mui/material'
import CrossSectionInput from '@editor/components/CrossSectionInput'
import useElementType from '@editor/hooks/useElementType'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { Section } from '@ui2/structure'
import { useSnackbar } from '@ui/snackbar'
import { getDefaultCheckSettingsForElement } from '@queries'
import { formatCmFromMeters } from '../utils'

const materialCategoryFromCrossSection = (cs: CrossSection): MaterialKind =>
  cs.kind === 'Steel' ? 'Steel' : cs.material.kind

const snapshotCrossSection = (cs: CrossSection): CrossSection => cloneDeep(cs)

const snapshotMemberSettings = (s: SettingsOnMember | null): SettingsOnMember | null =>
  s ? cloneDeep(s) : null

const isTimberOrSteelCrossSection = (
  cs: CrossSectionTimberOrCLTOrSteel | null | undefined,
): cs is CrossSection => cs != null && (cs.kind === 'Timber' || cs.kind === 'Steel')

export type MaterialSectionProps = {
  elementGuid?: string
  /** Plattenbalken / Dachplattenbalken: Querschnitt stammt aus der Plattenbaugruppe. */
  disableAllFields?: boolean
  disableForm?: boolean
}

const MaterialSummary = (): ReactElement => {
  const crossSection = useWatch({
    name: 'elements.0.crossSection',
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined

  if (!crossSection) {
    return (
      <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
        —
      </Box>
    )
  }

  if (crossSection.kind === 'Steel' && crossSection.shape.kind_literal === 'SteelCS') {
    return (
      <Box
        component="span"
        sx={{
          fontSize: 12,
          color: 'text.primary',
          whiteSpace: 'nowrap',
        }}
      >
        {crossSection.shape.profile} {crossSection.material.identifier}
      </Box>
    )
  }

  if (crossSection.kind === 'Timber' && crossSection.shape.kind_literal === 'RectangularCS') {
    const width = formatCmFromMeters(crossSection.shape.width)
    const height = formatCmFromMeters(crossSection.shape.height)
    const id = 'identifier' in crossSection.material ? crossSection.material.identifier : ''
    const strengthClass = renameSWxxToCxxIfApplicable(id)
    return (
      <Box
        component="span"
        sx={{
          fontSize: 12,
          color: 'text.primary',
          whiteSpace: 'nowrap',
        }}
      >
        {width}/{height} {strengthClass}
      </Box>
    )
  }

  return (
    <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
      —
    </Box>
  )
}

export const MaterialSection = ({
  elementGuid,
  disableAllFields = false,
  disableForm = false,
}: MaterialSectionProps): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const elementType = useElementType(elementGuid)
  const crossSectionFieldPath = 'elements.0.crossSection'
  const memberSettingsFieldPath = 'elements.0.memberCheckSettingsSelectedElement'

  const { setValue, getValues } = useFormContext<FieldValues>()
  const crossSection = useWatch({
    name: crossSectionFieldPath,
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined

  const inputsLocked = disableAllFields || disableForm || !crossSection
  const materialSection = useResultsSectionOpen('results2.design.material')

  const handleMaterialCategoryChange = async (
    prevCs: CrossSectionTimberOrCLTOrSteel,
    nextCs: CrossSectionTimberOrCLTOrSteel,
  ) => {
    if (disableAllFields) return

    const prevSettings = getValues(memberSettingsFieldPath) as SettingsOnMember | null
    if (!prevCs || !isTimberOrSteelCrossSection(prevCs)) return

    const nextKind = 'material' in nextCs ? nextCs.material.kind : undefined

    if (nextKind && materialCategoryFromCrossSection(prevCs) === nextKind) return

    const prevCsSnapshot = snapshotCrossSection(prevCs)
    const prevSettingsSnapshot = snapshotMemberSettings(prevSettings)

    try {
      const crossesTimberSteel = prevCs.kind !== nextCs.kind

      if (crossesTimberSteel && projectId && elementGuid) {
        try {
          const data = await getDefaultCheckSettingsForElement.request(
            projectId,
            elementGuid,
            nextCs,
          )
          setValue(memberSettingsFieldPath, data.new_settings, {
            shouldDirty: true,
            shouldTouch: true,
          })
          setValue(crossSectionFieldPath, data.new_cs, { shouldDirty: true, shouldTouch: true })
          enqueueSnackbar(
            'Der Materialtyp wurde geändert, bitte Nachweiseinstellungen genau prüfen',
            { variant: 'info' },
          )
        } catch {
          setValue(crossSectionFieldPath, prevCsSnapshot, { shouldDirty: false })
          if (prevSettingsSnapshot) {
            setValue(memberSettingsFieldPath, prevSettingsSnapshot, {
              shouldDirty: false,
            })
          }
          enqueueSnackbar(
            'Querschnitt konnte nicht gewechselt werden. Nachweiseinstellungen unverändert.',
            { variant: 'error' },
          )
        }
      }
    } catch {
      enqueueSnackbar('Materialtyp konnte nicht gewechselt werden.', { variant: 'error' })
    }
  }

  return (
    <Section
      title="Material & Querschnitt"
      collapsible
      hideBorderBottom
      summary={<MaterialSummary />}
      {...materialSection}
    >
      <Box sx={{ pt: 1 }}>
        <CrossSectionInput
          crossSectionFieldPath={crossSectionFieldPath}
          onChangeMaterialCategory={handleMaterialCategoryChange}
          disabled={inputsLocked}
          elementType={elementType}
        />
      </Box>
    </Section>
  )
}
